import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePublicPlans } from "@/api/queries/publicPlans";

/**
 * What hand-quoting costs, in the visitor's own numbers.
 *
 * Paperless Parts, Tempus Tools and Oroox all put a calculator like
 * this on their marketing site; the landing had nothing a shop owner
 * could put their own figures into.
 *
 * 🚨 It multiplies the visitor's two inputs and stops there. No claim
 * about revenue, win rates or orders recovered — we have no data behind
 * any of those, and a number the platform cannot back is the class of
 * promise this repo has spent a year removing. The one thing it says
 * beyond arithmetic is qualitative ("some of them arrive when nobody is
 * at the desk"), because the share of after-hours enquiries would be a
 * measurement and we do not have it yet.
 *
 * The bar under it (KAN-1329) puts the same hours against a 160-hour
 * working month — a fixed yardstick, not a claim about the visitor.
 *
 * KAN-1944: the default minutes match the "three quarters of an hour" the
 * comparison table above quotes for one quote — two numbers on one page
 * must not disagree. A third input turns the hours into money at the
 * visitor's OWN hourly cost, in the currency the pricing page bills in
 * (``/public/plans``). It is pre-filled from ``savings.defaultRate`` — an
 * amount in an admin-editable content key, not a constant here. The amount
 * is denominated in the PLATFORM base currency, which ``/public/plans``
 * already carries as ``platform_fee_cap_currency``; the key itself names no
 * currency (``currencyInLabels`` forbids that in ``common``). It is used only
 * when the plans bill in that same currency: the platform shell has no
 * exchange rate to convert with, so in any other currency the field stays
 * empty rather than showing a number in the wrong money.
 */
const DEFAULTS = { quotes: 30, minutes: 45 } as const;
const MAX = { quotes: 400, minutes: 180, rate: 100_000 } as const;
const WORK_MONTH_HOURS = 160;

/** The content key's amount; anything that is not a positive number ⇒ none. */
function parseDefaultRate(raw: string): number | null {
  const n = Number(raw.trim().replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function clamp(raw: string, max: number): number {
  const n = Number(raw.replace(/\s/g, ""));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(Math.floor(n), max);
}

export function PlatformSavings({ lang }: { lang: string }): JSX.Element {
  const { t } = useTranslation("common");
  const [quotes, setQuotes] = useState<number>(DEFAULTS.quotes);
  const [minutes, setMinutes] = useState<number>(DEFAULTS.minutes);
  // ``undefined`` = the visitor has not touched the field yet.
  const [typedRate, setRate] = useState<number | null | undefined>(undefined);
  const plans = usePublicPlans();
  const first = plans.data?.items[0];
  const currency = first?.currency ?? null;
  const defaultRate = parseDefaultRate(t("platformLanding.savings.defaultRate"));
  const prefill =
    defaultRate !== null && first && first.currency === first.platform_fee_cap_currency
      ? defaultRate
      : null;
  const rate = typedRate === undefined ? prefill : typedRate;

  const hours = (quotes * minutes) / 60;

  // One decimal below ten hours, whole hours above: "1,5 h" is useful,
  // "62,5 h" is false precision on an estimate.
  //
  // Formatted here rather than through i18next ``count``: that is plural
  // machinery, and it resolves 12.5 to a plural category and prints the
  // rounded integer — so the decimal the calculation exists to show
  // disappeared. One key, one number, formatted for the page's language.
  const shown = useMemo(
    () =>
      hours.toLocaleString(lang, {
        maximumFractionDigits: hours < 10 ? 1 : 0,
      }),
    [hours, lang],
  );
  const cost = useMemo((): string | null => {
    if (!rate || !currency) return null;
    try {
      return new Intl.NumberFormat(lang, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(hours * rate);
    } catch {
      return null;
    }
  }, [hours, rate, currency, lang]);
  const fill = Math.max(1.5, Math.min(100, (hours / WORK_MONTH_HOURS) * 100));

  return (
    <section className="section center" data-testid="platform-savings">
      <div className="inner tight">
        <p className="kicker">{t("platformLanding.savings.kicker")}</p>
        <h2 className="h2">{t("platformLanding.savings.heading")}</h2>

        <div className="calc">
          <label>
            <span>{t("platformLanding.savings.quotesLabel")}</span>
            <input
              type="text"
              inputMode="numeric"
              value={quotes}
              onChange={(e) => setQuotes(clamp(e.target.value, MAX.quotes))}
              data-testid="savings-quotes"
            />
          </label>

          <label>
            <span>{t("platformLanding.savings.minutesLabel")}</span>
            <input
              type="text"
              inputMode="numeric"
              value={minutes}
              onChange={(e) => setMinutes(clamp(e.target.value, MAX.minutes))}
              data-testid="savings-minutes"
            />
          </label>

          <label>
            <span>{t("platformLanding.savings.rateLabel")}</span>
            <input
              type="text"
              inputMode="numeric"
              value={rate ?? ""}
              placeholder={t("platformLanding.savings.ratePlaceholder")}
              onChange={(e) =>
                setRate(
                  e.target.value.trim() === ""
                    ? null
                    : clamp(e.target.value, MAX.rate),
                )
              }
              data-testid="savings-rate"
            />
          </label>

          <output data-testid="savings-result" className="calc-out">
            <span>{t("platformLanding.savings.resultLabel")}</span>
            <b>{t("platformLanding.savings.hours", { hours: shown })}</b>
          </output>
        </div>

        <p className="calc-cost" data-testid="savings-cost">
          {cost
            ? t("platformLanding.savings.cost", { money: cost })
            : t("platformLanding.savings.costPrompt")}
        </p>
        <p className="calc-note">{t("platformLanding.savings.footnote")}</p>
        <div className="bar" aria-hidden="true">
          <i style={{ width: `${fill}%` }} />
        </div>
        <div className="bar-legend" aria-hidden="true">
          <span>{t("platformLanding.savings.barHours", { hours: shown })}</span>
          <span>
            {t("platformLanding.savings.barMonth", {
              hours: WORK_MONTH_HOURS.toLocaleString(lang),
            })}
          </span>
        </div>
      </div>
    </section>
  );
}
